import { Button, Modal, useToast } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { healthCheckOptions } from "@/api";
import { Text } from "@/components/Text";
import { type AppVersion, aboutDetails, appVersion, isMismatch, versionLine } from "./appVersion";

function Row({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <Text as="dt" variant="body-sm" tone="secondary">
        {label}
      </Text>
      <Text as="dd" variant="code" className="text-right break-all">
        {value}
      </Text>
    </div>
  );
}

/** Which version of AnthmAI this is: the UI's version and release, and the server's (MHS-1079). */
export function AboutModal({ onClose }: Readonly<{ onClose: () => void }>) {
  const { t } = useTranslation("shell", { keyPrefix: "about" });
  const toast = useToast();
  const app = appVersion();
  const health = useQuery({ ...healthCheckOptions(), staleTime: 0 });
  const server: AppVersion | null = health.data
    ? { version: health.data.version, release: health.data.release ?? "" }
    : null;
  const serverStatus = health.isPending ? t("checking") : t("unavailable");
  const serverValue = server ? versionLine(server) : serverStatus;

  const copy = () => {
    const text = aboutDetails(app, server, { app: t("app"), server: t("server"), unavailable: t("unavailable") });
    navigator.clipboard.writeText(text).then(
      () => toast.success(t("copied")),
      () => toast.error(t("copyFailed")),
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("title")}
      closeLabel={t("close")}
      actions={
        <>
          <Button variant="secondary" onClick={copy}>
            {t("copy")}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {t("done")}
          </Button>
        </>
      }
    >
      <dl className="divide-y divide-border-subtle">
        <Row label={t("version")} value={app.version} />
        <Row label={t("release")} value={app.release || t("localBuild")} />
        <Row label={t("server")} value={serverValue} />
      </dl>
      {isMismatch(app, server) && (
        <Text variant="body-sm" tone="warning" className="mt-3">
          {t("mismatch")}
        </Text>
      )}
      <Text variant="body-sm" tone="secondary" className="mt-4">
        <Link to="/help" onClick={onClose} className="underline underline-offset-2 btn-focus-ring">
          {t("help")}
        </Link>
      </Text>
    </Modal>
  );
}
